import { useState, useEffect } from "react"
import { useStores } from "hooks/useStores"
import { inject, observer } from "mobx-react"

import TypographyPro from "themes/TypographyPro"
import { getTranslatedTextByKey, getLocaleStr, codeToLocale } from "utils/language"

import type _User from 'mobx/User'
import type _Infra from 'mobx/Infra'
import { FlowStep } from "mobx/Localization/types"

import { EditingContainer, EditingHeader, EditingBody, DeliveryIcon, PickupIcon, StyledCloseIcon, MapMarkerIcon, StoreIcon, Divider } from '../style'
import EditingRow from "./Row";

const EditingView = ({User, Infra} : {User: typeof _User, Infra: typeof _Infra}) => {

    const [showEditOrderTypeButton, setShowEditOrderTypeButton] = useState(false)
    const [showEditStoreButton, setShowEditStoreButton] = useState(false)
    const { localizationStore } = useStores()

    useEffect(() => {
        const shouldShowEditOrderTypeButton = async() => {
            const skipOrderTypeSelection = localizationStore.shouldSkipOrderTypeSelection()
            setShowEditOrderTypeButton(!skipOrderTypeSelection)
        }
        shouldShowEditOrderTypeButton()
    }, [])

    useEffect(() => {
        const shouldShowEditStoreButton = async() => {
            const stores = await localizationStore.availableStoresOnActualAddress()
            setShowEditStoreButton(stores.length > 1)
        }
        shouldShowEditStoreButton()
    }, [])

    const preferredLanguage = User?.preferredLanguage || 'en'
    const langFromInfra = Infra?.appParams?.l || 'en'
    const locale = User?.preferredLanguage ? codeToLocale[preferredLanguage] : codeToLocale[langFromInfra]
    const deliveryType = localizationStore.actualFlow?.orderTypeFlow ? getTranslatedTextByKey(localizationStore.actualFlow.orderTypeFlow.orderTypeTranslationKey, localizationStore.actualFlow.orderTypeFlow.orderType) : ''
    const canGoToAddressSelectionStep = localizationStore.canGoToStep(FlowStep.AddressSelection)
    const canGoToStoreMapSelectionStep = localizationStore.canGoToStep(FlowStep.StoreOnMapSelection)

    return (
        <EditingContainer>
            <EditingHeader>
                <TypographyPro variant="SubheadHighlighted">{getTranslatedTextByKey('webviewFlow.localization.EditAddress', 'Edit Address')}</TypographyPro>
                <StyledCloseIcon data-testid="close-button" onClick={localizationStore.abort} />
            </EditingHeader>
            <EditingBody>
                <EditingRow
                    title={getTranslatedTextByKey('keywords.editOptions.deliveryType', 'Ordering method')} 
                    icon={localizationStore.actualFlow?.orderTypeFlow?.requireAddress ? <DeliveryIcon /> : <PickupIcon />}
                    description={deliveryType.toUpperCase()}
                    action={showEditOrderTypeButton ? localizationStore.start : undefined}
                />
                <Divider />

                {localizationStore.actualFlow?.orderTypeFlow?.requireAddress &&
                <>
                    <EditingRow 
                        title={getTranslatedTextByKey('webviewFlow.deliveryAddress', 'Delivery address')}
                        icon={<MapMarkerIcon />}
                        description={localizationStore.actualFlow?.address?.formatted || ''}
                        action={
                            canGoToAddressSelectionStep ? () => {localizationStore.editAction(FlowStep.AddressSelection)} : 
                            canGoToStoreMapSelectionStep ? () => {
                                localizationStore.editAction(FlowStep.StoreOnMapSelection)
                            } : undefined
                        }
                    />
                    <Divider />
                </>
                }

                <EditingRow 
                    title={`${deliveryType} ${getTranslatedTextByKey('webviewFlow.localization.from', 'From')}`} 
                    icon={<StoreIcon />}
                    description={localizationStore.actualFlow?.store ? getLocaleStr(localizationStore.actualFlow.store.name, locale) : ''}
                    additionnalInfo={localizationStore.actualFlow?.store?.address || ''}
                    action={canGoToStoreMapSelectionStep && showEditStoreButton ? () => {localizationStore.editAction(FlowStep.StoreOnMapSelection)} : undefined}
                />
            </EditingBody>
        </EditingContainer>
    )
}

export default inject(
	'User',
    'Infra'
)(observer(EditingView))
